package er.prateek.crossly.ads

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The whole ad policy, carried as the `ads` section of
 * [er.prateek.crossly.config.AppConfig] -- see that class for how it is fetched and parsed.
 *
 * One section means a new lever -- a placement, a cap, a swapped unit id -- is a console edit
 * and a field with a default here, never a new Remote Config parameter that older installs
 * don't know how to read. Every field has a default, so a config written by a newer build
 * still parses on an older one (unknown keys are ignored, missing keys fall back).
 */
@Serializable
data class AdsConfig(
    /** Master kill switch. `false` stops every format everywhere, immediately. */
    val enabled: Boolean = true,
    /**
     * Serve Google's test inventory instead of real ads. Keep `true` until the AdMob/AdSense
     * accounts are live -- serving real ads to your own test devices is a policy violation.
     */
    @SerialName("test_mode") val testMode: Boolean = true,
    /**
     * Ask the networks for non-personalized ads. Wired to the existing analytics opt-out at
     * the call site, so a user who declined analytics isn't silently profiled for ads.
     */
    @SerialName("non_personalized") val nonPersonalized: Boolean = false,
    val banner: BannerConfig = BannerConfig(),
    val interstitial: InterstitialConfig = InterstitialConfig(),
    val rewarded: RewardedConfig = RewardedConfig(),
    /**
     * Live ad unit ids, per platform. Only consulted when [testMode] is `false` -- so the
     * real ids can be published to Remote Config ahead of the switch-over and verified in
     * isolation by flipping one boolean.
     */
    @SerialName("unit_ids") val unitIds: AdUnitOverrides = AdUnitOverrides(),
) {
    companion object {
        /** The compiled fallback, used until a fetched config says otherwise. */
        val DEFAULT = AdsConfig()
    }
}

@Serializable
data class BannerConfig(
    val enabled: Boolean = true,
    /**
     * Which screens carry a banner, as [AdPlacement.wireName] values. Unknown names are
     * ignored, so a placement can be published to the console before the build that has it
     * ships.
     */
    val placements: List<String> = listOf(
        AdPlacement.HOME.wireName,
        AdPlacement.LEVEL_SELECT.wireName,
        AdPlacement.RESULTS.wireName,
        AdPlacement.MATH_LEVEL_SELECT.wireName,
        AdPlacement.RIDDLE_LEVEL_SELECT.wireName,
    ),
)

@Serializable
data class InterstitialConfig(
    val enabled: Boolean = true,
    /**
     * Show at most one interstitial per this many completed puzzles. `1` means every puzzle,
     * `0` disables the counter gate (the time and cap gates still apply).
     */
    @SerialName("every_n_puzzles") val everyNPuzzles: Int = 3,
    /**
     * Puzzles a brand-new player finishes before the first interstitial is eligible. The
     * first session is where retention is won or lost; don't spend it on an ad break.
     */
    @SerialName("min_puzzles_before_first") val minPuzzlesBeforeFirst: Int = 2,
    /** Hard floor between two interstitials, regardless of how fast puzzles are solved. */
    @SerialName("min_seconds_between") val minSecondsBetween: Int = 120,
    /** `0` means unlimited. */
    @SerialName("max_per_session") val maxPerSession: Int = 4,
    /** `0` means unlimited. Survives app restarts, unlike [maxPerSession]. */
    @SerialName("max_per_day") val maxPerDay: Int = 12,
)

@Serializable
data class RewardedConfig(
    val enabled: Boolean = true,
    /** `0` means unlimited. */
    @SerialName("max_per_day") val maxPerDay: Int = 5,
)

/**
 * Per-platform live unit ids. Empty string means "no override" -- [AdUnitIds] then keeps
 * using the compiled test id, which is the safe direction to fail in.
 *
 * The Android ids are compiled in as the real production units, so a Remote Config outage
 * or a cold start before the first fetch still resolves to real inventory instead of empty
 * -- but [AdsConfig.testMode] is still the actual gate: these ids are inert everywhere,
 * debug included, until Remote Config explicitly sets `test_mode: false`.
 */
@Serializable
data class AdUnitOverrides(
    @SerialName("android_banner") val androidBanner: String = "ca-app-pub-9472193362322542/5367174997",
    @SerialName("android_interstitial") val androidInterstitial: String = "ca-app-pub-9472193362322542/8979116949",
    @SerialName("android_rewarded") val androidRewarded: String = "ca-app-pub-9472193362322542/4270600223",
    @SerialName("ios_banner") val iosBanner: String = "",
    @SerialName("ios_interstitial") val iosInterstitial: String = "",
    @SerialName("ios_rewarded") val iosRewarded: String = "",
    /** AdSense publisher id, `ca-pub-...`. */
    @SerialName("web_client") val webClient: String = "",
    /** AdSense ad slot id for the banner unit. */
    @SerialName("web_banner_slot") val webBannerSlot: String = "",
)
